import { useCallback, useEffect, useRef, useState } from "react";
import type { EmblaCarouselType } from "embla-carousel";
import { Carousel } from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";
import { Box, Flex, Text, UnstyledButton, rem } from "@mantine/core";
import { Link } from "react-router-dom";
import {
  CTA_BG,
  CarouselNavButton,
  DiscoverPromoSlideCardMobile,
  LINE_GRAY,
  NAVY,
  type DiscoverPromoCarouselBaseProps,
} from "./discoverPromoCarousel.shared";

export type DiscoverPromoCarouselMobileProps = DiscoverPromoCarouselBaseProps;

export default function DiscoverPromoCarouselMobile({
  slides,
  height = 70,
  autoplayDelayMs = 4500,
}: DiscoverPromoCarouselMobileProps) {
  const autoplay = useRef(
    Autoplay({
      delay: autoplayDelayMs,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: false,
    }),
  );

  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const multi = slides.length > 1;

  const onSelect = useCallback(() => {
    if (!embla) return;
    setActiveIndex(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("reInit", onSelect);
    embla.on("select", onSelect);
    return () => {
      embla.off("reInit", onSelect);
      embla.off("select", onSelect);
    };
  }, [embla, onSelect]);

  if (slides.length === 0) {
    return null;
  }

  const activeSlide = slides[activeIndex] ?? slides[0];
  const ctaText = activeSlide.ctaLabel ?? "Sign Up";

  return (
    <Flex
      w="100%"
      maw="100%"
      mih={height}
      h={height}
      align="stretch"
      wrap="nowrap"
      style={{
        borderRadius: rem(12),
        overflow: "hidden",
        boxShadow: "0 4px 16px rgba(15, 23, 42, 0.1)",
        background: "transparent",
        boxSizing: "border-box",
      }}
    >
      <Box
        pos="relative"
        flex={1}
        miw={0}
        mih={0}
        style={{ minHeight: height, backgroundColor: "#fff" }}
      >
        <Carousel
          height="100%"
          w="100%"
          withControls={false}
          withIndicators={false}
          loop={multi}
          plugins={multi ? [autoplay.current] : []}
          slideSize="100%"
          slideGap={0}
          align="start"
          containScroll="trimSnaps"
          getEmblaApi={setEmbla}
          styles={{
            root: { height: "100%", width: "100%", backgroundColor: "#fff" },
            viewport: { height: "100%", backgroundColor: "#fff" },
            container: { height: "100%", backgroundColor: "#fff" },
            slide: { height: "100%", backgroundColor: "#fff" },
          }}
        >
          {slides.map((slide) => (
            <Carousel.Slide key={slide.id}>
              <DiscoverPromoSlideCardMobile slide={slide} />
            </Carousel.Slide>
          ))}
        </Carousel>

        {multi ? (
          <>
            <Flex
              pos="absolute"
              inset={0}
              align="center"
              justify="space-between"
              px={rem(6)}
              style={{ pointerEvents: "none" }}
            >
              <CarouselNavButton
                direction="prev"
                label="Previous promotion"
                onClick={() => embla?.scrollPrev()}
                disabled={!embla}
              />
              <CarouselNavButton
                direction="next"
                label="Next promotion"
                onClick={() => embla?.scrollNext()}
                disabled={!embla}
              />
            </Flex>
            <Flex
              pos="absolute"
              left={0}
              right={0}
              bottom={rem(6)}
              justify="center"
              align="center"
              gap={rem(6)}
              style={{ pointerEvents: "none" }}
            >
              {slides.map((s, i) => (
                <UnstyledButton
                  key={s.id}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === activeIndex ? "true" : undefined}
                  onClick={() => embla?.scrollTo(i)}
                  w={6}
                  h={6}
                  p={0}
                  style={{
                    borderRadius: "50%",
                    background: i === activeIndex ? NAVY : LINE_GRAY,
                    flexShrink: 0,
                    pointerEvents: "auto",
                    border: "none",
                  }}
                />
              ))}
            </Flex>
          </>
        ) : null}
      </Box>

      <UnstyledButton
        component={Link}
        to="/signup"
        aria-label="Sign up"
        display="flex"
        style={{
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          flexShrink: 0,
          minWidth: rem(76),
          maxWidth: "26%",
          paddingInline: rem(8),
          background: CTA_BG,
          textDecoration: "none",
          color: "inherit",
          alignSelf: "stretch",
        }}
      >
        <Text fw={700} fz={rem(12)} c={NAVY} ta="center" lh={1.2} style={{ wordBreak: "break-word" }}>
          {ctaText}
        </Text>
      </UnstyledButton>
    </Flex>
  );
}
